import { fork, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import plugins from '../../clientPlugins';

/* Handler Import */
import {
    handleLogIn,
    handleGetIsLogged,
    handleLogOut,
    handleForgotPassword,
    handleNewRegistration,
    handleVerify2FA,
    handleSetup2FA,
    handleEnable2FA,
    handleDisable2FA,
    handleRegenerateBackupCodes,
    handleGetTrustedDevices,
    handleRevokeTrustedDevice,
    handleRevokeAllTrustedDevices,
} from './handlers/auth';
import { handleCheckEmailAvailability } from './handlers/register';
import { handleSystemConfiguration, handleConfigValue, handleSaveConfig, handleBackupConfig, handleRestoreConfig, handleTestAIConnection, handleGetAiUsage, handleTestHubspotConnection, handleTestIntuitConnection, handleGetWhatsAppStatus, handleGetWhatsAppQr, handleStartWhatsAppSession, handleLogoutWhatsAppSession, handleTestWhatsAppMessage } from './handlers/config';
import { handleGetClient, handleGetClients, handleGetClientBillingEntities, handleGetClientAccounts, handleCreateClient, handleUpdateClient, handleActivateClient, handleDeactivateClient, handleDeleteClient, handleRestoreClient, handleGetUnbilledCharges, handleAddClientDocument, handleUpdateClientDocument, handleDeleteClientDocument, handleRequestClientDocumentCopy, handleGetClientDocuments, handleGetClientDocumentScopes, handleGetClientDocumentTypes, handleAddClientDocumentChange, handleUpdateClientDocumentChange, handleDeleteClientDocumentChange, handleContactClient, handleGetClientFixedPriceRates, handleSetClientFixedPriceRate } from './handlers/client';
import { handleGetPluginAdminCatalog, handleGetPluginClients, handleGetPluginAvailableClients, handleEnablePluginClient, handleDisablePluginClient } from './handlers/pluginAdmin';
import { handleGetAccount, handleGetAccountProjects, handleGetAccountGroups, handleGetAccounts, handleGetAccountBillingEntities, handleFetchAccountUsers, handleCreateAccount, handleUpdateAccount, handleActivateAccount, handleDeactivateAccount, handleGetAccountMembers, handleAddAccountMembers, handleRemoveAccountMembers, handleDeleteAccount, handleRestoreAccount, handleGetClientAccountsTime } from './handlers/account';
import { handleGetProjectSummary, handleGetProject, handleGetProjects, handleGetProjectBillingEntities, handleSetProjectAssignees, handleSetProjectResponsible, handleSetProjectExternalBoard, handleCreateProject, handleUpdateProjectStatus, handleCompleteProject, handleDeleteProject, handleUpdateProject, handleRestoreProject, handleGetUnansweredClientComments, handleGetMonthlyStatusBreakdown, handleGetUnassignedSummary, handleGetUnassignedList, handleGetProjectMovePreview, handleMoveProject, handleAuthorizeProject, handleRejectProject } from './handlers/project';
import { handleGetProjectAiSummary, handleRequestProjectAiSummary, handleGetDailyActivityAiSummary, handleRequestDailyActivityAiSummary, handleGetWeeklyActivityAiSummary, handleRequestWeeklyActivityAiSummary, handleGetMonthlyActivityAiSummary, handleRequestMonthlyActivityAiSummary, handleGetEndOfDayChecklist, handleRegenerateQuote, handleGetSuggestions, handleGetSuggestionDetail, handleGetPendingSuggestionCount, handleApproveSuggestion, handleRejectSuggestion, handleGetSuggestionRecipients, handleRedirectSuggestion, handleGenerateSuggestions, handleAskAiQuestion, handleGetAiAnswer, handleGetAiConversation, handleExportAiConversation } from './handlers/ai';
import { handleGetTask, handleGetTasks, handleGetTasksByUser, handleSetTaskAssignees, handleSetTaskResponsible, handleSetUpdateTask, handleCreateTask, handleUpdateTaskStatus, handleDeleteTaskAssignee, handleCompleteTask, handleDeleteTask, handleRestoreTask, handleToggleTaskBoard, handleGetBoardCards, handleAddBoardCard, handleUpdateBoardCard, handleMoveBoardCardColumn, handleRemoveBoardCard, handleUpdateBoardColumns, handleAttachBoardCardAttachments, handleDetachBoardCardAttachment, handleRetryBoardCardPush, handleAddChecklistItem, handleRenameChecklistItem, handleRemoveChecklistItem, handleToggleChecklistItem, handleImportTaskChecklist, handleUpdateTaskBlockers, handleCompleteTaskWithBypass, handleUpdateTaskStatusWithBypass, handleGetSuggestedDueDate } from './handlers/task';
import { handleAddComment, handleEditComment, handleDeleteComment, handleRestoreComment } from './handlers/comment';
import { handleGetSkill, handleGetSkills, handleGetSkillUsage, handleCreateSkill, handleUpdateSkill, handleDeleteSkill, handleGetSkillRates, handleSaveSkillRates } from './handlers/skill';
import { handleCreateTime, handleDeleteTime, handleGetTimers, handleRestoreTime, handleUpdateTime, handleGetTimeByUserTotal, handleGetTimeProfile, handleGetTimeRecordsByUser, handleGetRunningTimer, handleGetRunningTimerByUser, handleGetRunningTimers, handleGetClientsWithRecentActivity } from './handlers/time';
import { handleGetActivities, handleGetAccountActivity, handleGetClientActivity, handleGetObjectActivity, handleGetUserActivity } from './handlers/activity';
import { handleGetDepartments, handleGetDepartment, handleGetDepartmentByUser, handleCreateDepartment, handleUpdateDepartment, handleAddUsersToDepartment, handleRemoveUsersFromDepartment, handleDeleteDepartment } from './handlers/departments';
import { handleGetUser, handleGetManagers, handleGetAllUsers, handleCreateUser, handleUpdateUser, handleUploadUserAvatar, handleRemoveUserAvatar, handleDeleteUser, handleRestoreUser, handleActivateUser, handleDeactivateUser, handleGetDeactivationImpact, handleUpdateUserPassword, handleAddUserAccounts, handleRemoveUserAccounts, handleUpdateUserAccountRole, handleUpdateUserRole, handleUpdateUserEmail, handleGetUserAccessFlags, handleSetUserApiAccess, handleUserAssignments, handleUpdatePinnedAccounts, handleToggleStarredItem, handleFetchMentionableServiceUsers } from './handlers/users';
import { handleAddTimeOff, handleEditTimeOff, handleReviewTimeOff, handleDeleteTimeOff, handleGetTimeOffSummary, handleGetCurrentLeave, handleGetPendingTimeOff, handleGetCurrentlyOut, handleGetTimeOffOverlaps } from './handlers/timeoff';
import { handleGetHolidays, handleAddHoliday, handleEditHoliday, handleDeleteHoliday } from './handlers/holidays';
import { handleGetCharges, handleGetCharge, handleCreateCharge, handleUpdateCharge, handleDeleteCharge, handleActivateCharge, handleDeactivateCharge } from './handlers/charges';
import { handleGetInvoices, handleGetInvoice, handleCreateInvoice, handleUpdateInvoice, handleUpdateInvoiceLineItems, handleSendInvoice, handleCancelInvoice, handleUpdateInvoiceStatus, handleCreateCreditMemo, handleExportInvoicesIif, handleGenerateLastMonthInvoices, handleGetInvoicesByBillingEntities, handleSendInvoiceToGateway, handleDeleteInvoice, handleRecordClientPayment, handleGetClientsWithRecentInvoices, handleGetInactiveClients, handleGetInvoiceSummary, handleGetClientBillingOverview, handleSendAllDraftInvoices, handleGetInvoiceOrphans, handleAddInvoiceOrphans, handleGetPdfTemplateConfig, handleSavePdfTemplateConfig, handleUploadPdfTemplate, handleDownloadInvoicePdf, handleGetIntuitCustomers,
    handleGetIntuitConnectionStatus, handleGetInvoicedPeriodForDate, handleGetUnbilledReport, handleGetBillingPreview, handleGetInvoiceNumberPreview } from './handlers/invoices';
import { handleSearchBillingEntities, handleToggleBillingEntity } from './handlers/billingEntities';
import { handleGetBillingRetainers, handleSaveBillingRetainerTerms, handleGetRetainerStatus } from './handlers/billingRetainers';
import {
    handleGetBillingEmployees,
    handleUpdateEmployeeSalaryData,
    handleSetEmployeeTerminated,
    handleGetEmployeePayments,
    handleCreateEmployeePayment,
    handleUpdateEmployeePayment,
    handleDeleteEmployeePayment
} from './handlers/billingEmployees';
import { handleGetFixedPricePayments, handleUpdateFixedPricePayment, handleDeleteFixedPricePayment, handleClearFixedPricePayments, handleGetUnconfiguredFixedPriceEntities } from './handlers/fixedPricePayments';
import { handleGetMasterReport, handleGetMasterReportTask, handleGetReportClients, handleGetReportAccounts, handleGetReportUsers, handleGetRetainers, handleGetProjectProfitability, handleGetResourceUtilization, handleGetDailyHours, handleGetMyDailyHours, handleGetReportDepartments, handleGetArAging, handleGetProfitabilityTrend, handleGetHoursPosted } from './handlers/reports';
import { handleGetRoles, handleGetRole, handleGetRoleExpandedPermissions, handleGetRoleUsers, handleCreateRole, handleUpdateRole, handleDeleteRole } from './handlers/role';
import { handleGetPermissions, handleGetVisibilityCheck } from './handlers/permission';
import { handleGetGroup, handleGetGroups, handleGetGroupBillingEntities, handleUpdateGroup, handleCreateGroup, handleCompleteGroup, handleDeleteGroup, handleRestoreGroup, handleAddProjectToGroup, handleRemoveProjectFromGroup } from './handlers/group';
import { handleGetTemplates, handleGetGroupTemplate, handleGetProjectTemplate, handleGetTaskTemplate, handleCreateGroupTemplate, handleCreateProjectTemplate, handleCreateTaskTemplate, handleUpdateGroupTemplate, handleUpdateProjectTemplate, handleUpdateTaskTemplate, handleDeleteGroupTemplate, handleDeleteProjectTemplate, handleDeleteTaskTemplate, handleExecuteGroupTemplate, handleExecuteProjectTemplate, handleGetTemplateImportSource, handleImportTemplate, handleGetChecklistTemplate, handleCreateChecklistTemplate, handleUpdateChecklistTemplate, handleDeleteChecklistTemplate, handleGetQuoteTemplate, handleCreateQuoteTemplate, handleUpdateQuoteTemplate, handleDeleteQuoteTemplate, handleCreateQuoteTemplateFromQuote } from './handlers/template';
import { handleGetNotifications,
    handleGetUnreadCount, handleMarkNotificationRead, handleMarkAllNotificationsRead, handleDeleteNotification, handleSendCustomNotification } from './handlers/notification';
import { handleSearch } from './handlers/search';
import { handleGetQuotes, handleGetQuote, handleCreateQuote, handleUpdateQuote, handleApproveQuote, handleRejectQuote, handleAcceptQuote, handleDeclineQuote, handlePreviewQuotePdf, handleReopenQuote, handleCloneQuote } from './handlers/quote';
import { handleGetServiceAccounts, handleCreateServiceAccount, handleRotateServiceAccount, handleActivateServiceAccount, handleDeactivateServiceAccount, handleDeleteServiceAccount } from './handlers/serviceAccount';
import { handleGetApiKeyScopes, handleGetMyApiKeys, handleCreateApiKey, handleRevokeMyApiKey, handleGetAllApiKeys, handleGetUserApiKeys, handleRevokeUserApiKey, handleDeleteUserApiKey } from './handlers/apiKey';
import { handleGetVapidPublicKey, handleSubscribePush, handleUnsubscribePush, handleGetPushStatus } from './handlers/push';
import { handleGetInboundMail, handleResolveInboundMail, handleGetInboundMailOauthStatus } from './handlers/inboundMail';
import { handleGetTags, handleGetAssignableTags, handleCreateTag, handleUpdateTag, handleDeleteTag, handleRestoreTag, handleGetTagUsage, handleSetTagAssignments, handleGetInheritedTags, handleGetTagDetail, handleUpdateTagContext } from './handlers/tag';
import { handleUploadInlineImage } from './handlers/attachment';
import {
    handleGetCalendarStatus,
    handleGetMeetings,
    handleGetManagerReport,
    handleScheduleMeeting,
    handleGetMeetingSlots,
    handleRecordMeetingTime,
    handleSetMeetingManualTrack,
    handleGetManagerReportDrilldown,
    handleSetUserCalendarOptOut,
} from './handlers/calendar';

import { handleGetBugherdContext, handleGetBugherdBoards, handleSyncBugherdBoard, handleDisconnectBugherd } from './handlers/bugherd';
import { handleGetKBTags, handleGetKBArticles, handleGetKBArticle, handleCreateKBArticle, handleCreateKBArticleFromProject, handleRequestKBAIRewrite, handleUpdateKBArticle, handleDeleteKBArticle, handleGetKBArticleVersions } from './handlers/kb';

/* Slice Action Imports */
import { logIn, logOut, isLoggedIn, forgotPassword, newRegistration } from "../slices/auth"
import { fetchAccountUsers, fetchUserAssignments, updatePinnedAccounts, toggleStarredItem, fetchMentionableServiceUsers } from '../slices/users';
import { getConfig, getConfigValue, saveConfig, backupConfig, restoreConfig, testAIConnection, getAiUsage, testHubspotConnection, testIntuitConnection, getWhatsAppStatus, getWhatsAppQr, startWhatsAppSession, logoutWhatsAppSession, testWhatsAppMessage } from '../slices/config';
import { getSkill, getSkills, getSkillUsage, createSkill, editSkill, removeSkill, getSkillRates, saveSkillRates } from '../slices/skill';
import { completeProject, createProject, deleteProject, getProject, getProjects, setProjectAssignees, setProjectResponsible, setProjectExternalBoard, updateProject, updateProjectStatus, restoreProject } from '../slices/project';
import { getUser, getManagers, getAllUsers, createUser, updateUser, uploadUserAvatar, removeUserAvatar, deleteUser, restoreUser, activateUser, deactivateUser, getDeactivationImpact, updateUserPassword, addUserAccounts, removeUserAccounts, updateUserRole, updateUserEmail, getUserAccessFlags, setUserApiAccess, updateUserAccountRole } from '../slices/users';
import { checkEmailAvailability } from '../slices/register';
import { getQuotes, getQuote, createQuote, updateQuote, approveQuote, rejectQuote, acceptQuote, declineQuote, previewQuotePdf, reopenQuote, cloneQuote } from '../slices/quote';
import { getCalendarStatus } from '../slices/calendar';
import { getPendingSuggestionCount } from '../slices/suggestion';

/* Saga Watcher for Actions */
export function* watcherSaga() {
    for (const plugin of plugins) {
        if (plugin.saga) yield fork(plugin.saga);
    }
    yield takeLatest(logIn.type, handleLogIn);
    yield takeLatest(isLoggedIn.type, handleGetIsLogged);
    yield takeLatest(logOut.type, handleLogOut);
    yield takeLatest(forgotPassword.type, handleForgotPassword);
    yield takeLatest(newRegistration.type, handleNewRegistration);
    yield takeLatest('VERIFY_2FA_REQUEST', handleVerify2FA);
    yield takeLatest('SETUP_2FA_REQUEST', handleSetup2FA);
    yield takeLatest('ENABLE_2FA_REQUEST', handleEnable2FA);
    yield takeLatest('DISABLE_2FA_REQUEST', handleDisable2FA);
    yield takeLatest('REGENERATE_BACKUP_CODES_REQUEST', handleRegenerateBackupCodes);
    yield takeLatest('GET_TRUSTED_DEVICES_REQUEST', handleGetTrustedDevices);
    yield takeLatest('REVOKE_TRUSTED_DEVICE_REQUEST', handleRevokeTrustedDevice);
    yield takeLatest('REVOKE_ALL_TRUSTED_DEVICES_REQUEST', handleRevokeAllTrustedDevices);

    yield takeLatest(getConfig.type, handleSystemConfiguration);
    yield takeEvery(getConfigValue.type, handleConfigValue);
    yield takeEvery(saveConfig.type, handleSaveConfig);
    yield takeEvery(backupConfig.type, handleBackupConfig);
    yield takeEvery(restoreConfig.type, handleRestoreConfig);
    yield takeEvery(testAIConnection.type, handleTestAIConnection);
    yield takeEvery(getAiUsage.type, handleGetAiUsage);
    yield takeEvery(testHubspotConnection.type, handleTestHubspotConnection);
    yield takeEvery(testIntuitConnection.type, handleTestIntuitConnection);
    yield takeEvery(getWhatsAppStatus.type, handleGetWhatsAppStatus);
    yield takeEvery(getWhatsAppQr.type, handleGetWhatsAppQr);
    yield takeEvery(startWhatsAppSession.type, handleStartWhatsAppSession);
    yield takeEvery(logoutWhatsAppSession.type, handleLogoutWhatsAppSession);
    yield takeEvery(testWhatsAppMessage.type, handleTestWhatsAppMessage);

    //  Skill Mappings
    yield takeLatest(getSkill.type, handleGetSkill);
    yield takeLatest(getSkills.type, handleGetSkills);
    yield takeEvery(getSkillUsage.type, handleGetSkillUsage);
    yield takeLatest(createSkill.type, handleCreateSkill);
    yield takeLatest(editSkill.type, handleUpdateSkill);
    yield takeLatest(removeSkill.type, handleDeleteSkill);
    yield takeLatest(getSkillRates.type, handleGetSkillRates);
    yield takeEvery(saveSkillRates.type, handleSaveSkillRates);

    // Client Mappings
    yield takeLatest('GET_CLIENT_REQUEST', handleGetClient);
    yield takeEvery('GET_CLIENTS_REQUEST', handleGetClients);
    yield takeLatest('GET_CLIENT_BILLING_ENTITIES_REQUEST', handleGetClientBillingEntities);
    yield takeEvery('GET_CLIENT_ACCOUNTS_REQUEST', handleGetClientAccounts);
    yield takeLatest('CREATE_CLIENT_REQUEST', handleCreateClient);
    yield takeLatest('UPDATE_CLIENT_REQUEST', handleUpdateClient);
    yield takeLatest('ACTIVATE_CLIENT_REQUEST', handleActivateClient);
    yield takeLatest('DEACTIVATE_CLIENT_REQUEST', handleDeactivateClient);
    yield takeLatest('DELETE_CLIENT_REQUEST', handleDeleteClient);
    yield takeLatest('RESTORE_CLIENT_REQUEST', handleRestoreClient);
    yield takeLatest('GET_UNBILLED_CHARGES_REQUEST', handleGetUnbilledCharges);
    yield takeLatest('ADD_CLIENT_DOCUMENT_REQUEST', handleAddClientDocument);
    yield takeLatest('UPDATE_CLIENT_DOCUMENT_REQUEST', handleUpdateClientDocument);
    yield takeLatest('DELETE_CLIENT_DOCUMENT_REQUEST', handleDeleteClientDocument);
    yield takeLatest('REQUEST_CLIENT_DOCUMENT_COPY_REQUEST', handleRequestClientDocumentCopy);
    yield takeLatest('GET_CLIENT_DOCUMENTS_REQUEST', handleGetClientDocuments);
    yield takeLatest('GET_CLIENT_DOCUMENT_SCOPES_REQUEST', handleGetClientDocumentScopes);
    yield takeEvery('GET_CLIENT_DOCUMENT_TYPES_REQUEST', handleGetClientDocumentTypes);
    yield takeLatest('ADD_CLIENT_DOCUMENT_CHANGE_REQUEST', handleAddClientDocumentChange);
    yield takeLatest('UPDATE_CLIENT_DOCUMENT_CHANGE_REQUEST', handleUpdateClientDocumentChange);
    yield takeLatest('DELETE_CLIENT_DOCUMENT_CHANGE_REQUEST', handleDeleteClientDocumentChange);
    yield takeLatest('CONTACT_CLIENT_REQUEST', handleContactClient);
    yield takeLatest('GET_CLIENT_FIXED_PRICE_RATES_REQUEST', handleGetClientFixedPriceRates);
    yield takeEvery('SET_CLIENT_FIXED_PRICE_RATE_REQUEST', handleSetClientFixedPriceRate);

    // Plugin Admin Mappings
    yield takeEvery('GET_PLUGIN_ADMIN_CATALOG_REQUEST', handleGetPluginAdminCatalog);
    yield takeEvery('GET_PLUGIN_CLIENTS_REQUEST', handleGetPluginClients);
    yield takeEvery('GET_PLUGIN_AVAILABLE_CLIENTS_REQUEST', handleGetPluginAvailableClients);
    yield takeEvery('ENABLE_PLUGIN_CLIENT_REQUEST', handleEnablePluginClient);
    yield takeEvery('DISABLE_PLUGIN_CLIENT_REQUEST', handleDisablePluginClient);

    // Account Mappings
    yield takeEvery('GET_ACCOUNTS_REQUEST', handleGetAccounts);
    yield takeLatest('GET_CLIENT_ACCOUNTS_TIME_REQUEST', handleGetClientAccountsTime);
    yield takeLatest('GET_ACCOUNT_BILLING_ENTITIES_REQUEST', handleGetAccountBillingEntities);
    yield takeLatest('GET_ACCOUNT_REQUEST', handleGetAccount);
    yield takeEvery('GET_ACCOUNT_PROJECTS_REQUEST', handleGetAccountProjects);
    yield takeLatest('GET_ACCOUNT_GROUPS_REQUEST', handleGetAccountGroups);
    yield takeLatest('CREATE_ACCOUNT_REQUEST', handleCreateAccount);
    yield takeLatest('UPDATE_ACCOUNT_REQUEST', handleUpdateAccount);
    yield takeLatest('ACTIVATE_ACCOUNT_REQUEST', handleActivateAccount);
    yield takeLatest('DEACTIVATE_ACCOUNT_REQUEST', handleDeactivateAccount);
    yield takeLatest('GET_ACCOUNT_MEMBERS_REQUEST', handleGetAccountMembers);
    yield takeLatest('ADD_ACCOUNT_MEMBERS_REQUEST', handleAddAccountMembers);
    yield takeLatest('REMOVE_ACCOUNT_MEMBERS_REQUEST', handleRemoveAccountMembers);
    yield takeLatest('DELETE_ACCOUNT_REQUEST', handleDeleteAccount);
    yield takeLatest('RESTORE_ACCOUNT_REQUEST', handleRestoreAccount);
    yield takeLatest(fetchAccountUsers.type, handleFetchAccountUsers);
    yield takeLeading(fetchMentionableServiceUsers.type, handleFetchMentionableServiceUsers);

    // Group Mappings
    yield takeLatest('GET_GROUP_REQUEST', handleGetGroup);
    yield takeLatest('GET_GROUPS_REQUEST', handleGetGroups);
    yield takeLatest('GET_GROUP_BILLING_ENTITIES_REQUEST', handleGetGroupBillingEntities);
    yield takeLatest('UPDATE_GROUP_REQUEST', handleUpdateGroup);
    yield takeLatest('CREATE_GROUP_REQUEST', handleCreateGroup);
    yield takeLatest('COMPLETE_GROUP_REQUEST', handleCompleteGroup);
    yield takeLatest('DELETE_GROUP_REQUEST', handleDeleteGroup);
    yield takeLatest('RESTORE_GROUP_REQUEST', handleRestoreGroup);
    yield takeLatest('ADD_PROJECT_TO_GROUP_REQUEST', handleAddProjectToGroup);
    yield takeLatest('REMOVE_PROJECT_FROM_GROUP_REQUEST', handleRemoveProjectFromGroup);

    // Project Mappings
    yield takeLatest('GET_PROJECT_SUMMARY_REQUEST', handleGetProjectSummary);
    yield takeLatest(getProject.type, handleGetProject);
    yield takeLatest('GET_PROJECT_REQUEST', handleGetProject);
    yield takeLatest(getProjects.type, handleGetProjects);
    yield takeLatest('GET_PROJECTS_REQUEST', handleGetProjects);
    yield takeLatest('GET_PROJECT_BILLING_ENTITIES_REQUEST', handleGetProjectBillingEntities);
    yield takeLatest('GET_UNANSWERED_CLIENT_COMMENTS_REQUEST', handleGetUnansweredClientComments);
    yield takeLatest('GET_MONTHLY_STATUS_BREAKDOWN_REQUEST', handleGetMonthlyStatusBreakdown);
    yield takeEvery('GET_UNASSIGNED_SUMMARY_REQUEST', handleGetUnassignedSummary);
    yield takeLatest('GET_UNASSIGNED_LIST_REQUEST', handleGetUnassignedList);

    // AI Mappings
    yield takeLatest('GET_PROJECT_AI_SUMMARY_REQUEST', handleGetProjectAiSummary);
    yield takeEvery('REQUEST_PROJECT_AI_SUMMARY_REQUEST', handleRequestProjectAiSummary);
    yield takeLatest('GET_DAILY_ACTIVITY_AI_SUMMARY_REQUEST', handleGetDailyActivityAiSummary);
    yield takeEvery('REQUEST_DAILY_ACTIVITY_AI_SUMMARY_REQUEST', handleRequestDailyActivityAiSummary);
    yield takeLatest('GET_WEEKLY_ACTIVITY_AI_SUMMARY_REQUEST', handleGetWeeklyActivityAiSummary);
    yield takeEvery('REQUEST_WEEKLY_ACTIVITY_AI_SUMMARY_REQUEST', handleRequestWeeklyActivityAiSummary);
    yield takeLatest('GET_MONTHLY_ACTIVITY_AI_SUMMARY_REQUEST', handleGetMonthlyActivityAiSummary);
    yield takeEvery('REQUEST_MONTHLY_ACTIVITY_AI_SUMMARY_REQUEST', handleRequestMonthlyActivityAiSummary);
    yield takeLatest('GET_END_OF_DAY_CHECKLIST_REQUEST', handleGetEndOfDayChecklist);
    yield takeLatest('REGENERATE_QUOTE_REQUEST', handleRegenerateQuote);
    yield takeEvery('ASK_AI_QUESTION_REQUEST', handleAskAiQuestion);
    yield takeEvery('GET_AI_ANSWER_REQUEST', handleGetAiAnswer);
    yield takeEvery('GET_AI_CONVERSATION_REQUEST', handleGetAiConversation);
    yield takeLatest('EXPORT_AI_CONVERSATION_REQUEST', handleExportAiConversation);

    // Suggested Action Mappings
    yield takeLatest('GET_SUGGESTIONS_REQUEST', handleGetSuggestions);
    yield takeEvery('GET_SUGGESTION_DETAIL_REQUEST', handleGetSuggestionDetail);
    yield takeLatest(getPendingSuggestionCount.type, handleGetPendingSuggestionCount);
    yield takeEvery('APPROVE_SUGGESTION_REQUEST', handleApproveSuggestion);
    yield takeEvery('REJECT_SUGGESTION_REQUEST', handleRejectSuggestion);
    yield takeLatest('GET_SUGGESTION_RECIPIENTS_REQUEST', handleGetSuggestionRecipients);
    yield takeEvery('REDIRECT_SUGGESTION_REQUEST', handleRedirectSuggestion);
    yield takeEvery('GENERATE_SUGGESTIONS_REQUEST', handleGenerateSuggestions);

    yield takeLatest(updateProject.type, handleUpdateProject);
    yield takeLatest(setProjectExternalBoard.type, handleSetProjectExternalBoard);
    yield takeLatest('SET_PROJECT_DATA_REQUEST', handleUpdateProject);
    yield takeLatest(setProjectResponsible.type, handleSetProjectResponsible);
    yield takeLatest('SET_PROJECT_RESPONSIBLE_REQUEST', handleSetProjectResponsible);
    yield takeLatest(setProjectAssignees.type, handleSetProjectAssignees);
    yield takeLatest('SET_PROJECT_ASSIGNEES_REQUEST', handleSetProjectAssignees);
    yield takeLatest(createProject.type, handleCreateProject);
    yield takeLatest('CREATE_PROJECT_REQUEST', handleCreateProject);
    yield takeLatest(updateProjectStatus.type, handleUpdateProjectStatus);
    yield takeLatest('UPDATE_PROJECT_STATUS_REQUEST', handleUpdateProjectStatus);
    yield takeLatest(completeProject.type, handleCompleteProject);
    yield takeLatest('COMPLETE_PROJECT_REQUEST', handleCompleteProject);
    yield takeLatest(deleteProject.type, handleDeleteProject);
    yield takeLatest('DELETE_PROJECT_REQUEST', handleDeleteProject);
    yield takeLatest(restoreProject.type, handleRestoreProject);
    yield takeLatest('RESTORE_PROJECT_REQUEST', handleRestoreProject);
    yield takeLatest('GET_PROJECT_MOVE_PREVIEW_REQUEST', handleGetProjectMovePreview);
    yield takeLatest('MOVE_PROJECT_REQUEST', handleMoveProject);
    yield takeLatest('AUTHORIZE_PROJECT_REQUEST', handleAuthorizeProject);
    yield takeLatest('REJECT_PROJECT_REQUEST', handleRejectProject);

    // Task Mappings
    yield takeLatest('GET_TASK_REQUEST', handleGetTask);
    yield takeLatest('GET_TASKS_REQUEST', handleGetTasks);
    yield takeLatest('GET_TASKS_BY_USER_REQUEST', handleGetTasksByUser);
    yield takeLatest('GET_SUGGESTED_DUE_DATE_REQUEST', handleGetSuggestedDueDate);
    yield takeLatest('SET_TASK_DATA_REQUEST', handleSetUpdateTask);
    yield takeLatest('SET_TASK_RESPONSIBLE_REQUEST', handleSetTaskResponsible);
    yield takeLatest('SET_TASK_ASSIGNEES_REQUEST', handleSetTaskAssignees);
    yield takeLatest('CREATE_TASK_REQUEST', handleCreateTask);
    yield takeLatest('UPDATE_TASK_STATUS_REQUEST', handleUpdateTaskStatus);
    yield takeLatest('DELETE_TASK_ASSIGNEE_REQUEST', handleDeleteTaskAssignee);
    yield takeLatest('COMPLETE_TASK_REQUEST', handleCompleteTask);
    yield takeLatest('DELETE_TASK_REQUEST', handleDeleteTask);
    yield takeLatest('RESTORE_TASK_REQUEST', handleRestoreTask);
    yield takeLatest('TOGGLE_TASK_BOARD_REQUEST', handleToggleTaskBoard);
    yield takeEvery('GET_BOARD_CARDS_REQUEST', handleGetBoardCards);
    yield takeEvery('ADD_BOARD_CARD_REQUEST', handleAddBoardCard);
    yield takeEvery('UPDATE_BOARD_CARD_REQUEST', handleUpdateBoardCard);
    yield takeEvery('MOVE_BOARD_CARD_COLUMN_REQUEST', handleMoveBoardCardColumn);
    yield takeEvery('REMOVE_BOARD_CARD_REQUEST', handleRemoveBoardCard);
    yield takeEvery('UPDATE_BOARD_COLUMNS_REQUEST', handleUpdateBoardColumns);
    yield takeEvery('ATTACH_BOARD_CARD_ATTACHMENTS_REQUEST', handleAttachBoardCardAttachments);
    yield takeEvery('DETACH_BOARD_CARD_ATTACHMENT_REQUEST', handleDetachBoardCardAttachment);
    yield takeEvery('RETRY_BOARD_CARD_PUSH_REQUEST', handleRetryBoardCardPush);
    yield takeEvery('ADD_CHECKLIST_ITEM_REQUEST', handleAddChecklistItem);
    yield takeEvery('RENAME_CHECKLIST_ITEM_REQUEST', handleRenameChecklistItem);
    yield takeEvery('REMOVE_CHECKLIST_ITEM_REQUEST', handleRemoveChecklistItem);
    yield takeEvery('TOGGLE_CHECKLIST_ITEM_REQUEST', handleToggleChecklistItem);
    yield takeEvery('IMPORT_TASK_CHECKLIST_REQUEST', handleImportTaskChecklist);
    yield takeLatest('UPDATE_TASK_BLOCKERS_REQUEST', handleUpdateTaskBlockers);
    yield takeLatest('COMPLETE_TASK_WITH_BYPASS_REQUEST', handleCompleteTaskWithBypass);
    yield takeLatest('UPDATE_TASK_STATUS_WITH_BYPASS_REQUEST', handleUpdateTaskStatusWithBypass);

    // Time Mappings
    yield takeLatest('GET_RUNNING_TIMER_REQUEST', handleGetRunningTimer);
    yield takeLatest('GET_RUNNING_TIMERS_REQUEST', handleGetRunningTimers);
    yield takeLatest('GET_RUNNING_TIMER_BY_USER_REQUEST', handleGetRunningTimerByUser);
    yield takeLatest('GET_CLIENTS_WITH_RECENT_ACTIVITY_REQUEST', handleGetClientsWithRecentActivity);
    yield takeLatest('GET_TIME_REQUEST', handleGetTimers);
    yield takeLatest('GET_TIME_RECORDS_BY_USER_REQUEST', handleGetTimeRecordsByUser);
    yield takeLatest('GET_TIME_BY_USER_TOTAL_REQUEST', handleGetTimeByUserTotal);
    yield takeLatest('GET_TIME_PROFILE_REQUEST', handleGetTimeProfile);
    yield takeLatest('CREATE_TIME_REQUEST', handleCreateTime);
    yield takeLatest('UPDATE_TIME_REQUEST', handleUpdateTime);
    yield takeLatest('DELETE_TIME_REQUEST', handleDeleteTime);
    yield takeLatest('RESTORE_TIME_REQUEST', handleRestoreTime);

    // Comment Mappings
    yield takeLatest('ADD_COMMENT_REQUEST', handleAddComment);
    yield takeLatest('EDIT_COMMENT_REQUEST', handleEditComment);
    yield takeLatest('DELETE_COMMENT_REQUEST', handleDeleteComment);
    yield takeLatest('RESTORE_COMMENT_REQUEST', handleRestoreComment);

    // Activity Mappings
    yield takeLatest('GET_ACTIVITIES_REQUEST', handleGetActivities);
    yield takeLatest('GET_OBJECT_ACTIVITY_REQUEST', handleGetObjectActivity);
    yield takeLatest('GET_USER_ACTIVITY_REQUEST', handleGetUserActivity);
    yield takeLatest('GET_ACCOUNT_ACTIVITY_REQUEST', handleGetAccountActivity);
    yield takeLatest('GET_CLIENT_ACTIVITY_REQUEST', handleGetClientActivity);

    // Attachment Mappings
    yield takeEvery('UPLOAD_INLINE_IMAGE_REQUEST', handleUploadInlineImage);

    // Department Mappings
    yield takeEvery('GET_DEPARTMENTS_REQUEST', handleGetDepartments);
    yield takeLatest('GET_DEPARTMENT_REQUEST', handleGetDepartment);
    yield takeEvery('GET_DEPARTMENT_BY_USER_REQUEST', handleGetDepartmentByUser);
    yield takeLatest('CREATE_DEPARTMENT_REQUEST', handleCreateDepartment);
    yield takeLatest('UPDATE_DEPARTMENT_REQUEST', handleUpdateDepartment);
    yield takeLatest('ADD_USERS_TO_DEPARTMENT_REQUEST', handleAddUsersToDepartment);
    yield takeLatest('REMOVE_USERS_FROM_DEPARTMENT_REQUEST', handleRemoveUsersFromDepartment);
    yield takeLatest('DELETE_DEPARTMENT_REQUEST', handleDeleteDepartment);

    // User Mappings
    yield takeLatest(getUser.type, handleGetUser);
    yield takeLatest(getManagers.type, handleGetManagers);
    yield takeEvery(getAllUsers.type, handleGetAllUsers);
    yield takeLatest(createUser.type, handleCreateUser);
    yield takeLatest(updateUser.type, handleUpdateUser);
    yield takeEvery(uploadUserAvatar.type, handleUploadUserAvatar);
    yield takeEvery(removeUserAvatar.type, handleRemoveUserAvatar);
    yield takeLatest(deleteUser.type, handleDeleteUser);
    yield takeLatest(restoreUser.type, handleRestoreUser);
    yield takeLatest(activateUser.type, handleActivateUser);
    yield takeLatest(deactivateUser.type, handleDeactivateUser);
    yield takeLatest(getDeactivationImpact.type, handleGetDeactivationImpact);
    yield takeLatest(updateUserPassword.type, handleUpdateUserPassword);
    yield takeLatest(addUserAccounts.type, handleAddUserAccounts);
    yield takeLatest(removeUserAccounts.type, handleRemoveUserAccounts);
    yield takeLatest(updateUserAccountRole.type, handleUpdateUserAccountRole);
    yield takeLatest(updateUserRole.type, handleUpdateUserRole);
    yield takeLatest(updateUserEmail.type, handleUpdateUserEmail);
    yield takeLatest(getUserAccessFlags.type, handleGetUserAccessFlags);
    yield takeEvery(setUserApiAccess.type, handleSetUserApiAccess);
    yield takeLatest(fetchUserAssignments.type, handleUserAssignments);
    yield takeLatest(updatePinnedAccounts.type, handleUpdatePinnedAccounts);
    yield takeEvery(toggleStarredItem.type, handleToggleStarredItem);

    // Time Off Mappings
    yield takeLatest('GET_TIME_OFF_SUMMARY_REQUEST', handleGetTimeOffSummary);
    yield takeLatest('GET_CURRENT_LEAVE_REQUEST', handleGetCurrentLeave);
    yield takeLatest('GET_PENDING_TIME_OFF_REQUEST', handleGetPendingTimeOff);
    yield takeLatest('GET_CURRENTLY_OUT_REQUEST', handleGetCurrentlyOut);
    yield takeEvery('GET_TIME_OFF_OVERLAPS_REQUEST', handleGetTimeOffOverlaps);
    yield takeLatest('ADD_TIME_OFF_REQUEST', handleAddTimeOff);
    yield takeLatest('EDIT_TIME_OFF_REQUEST', handleEditTimeOff);
    yield takeLatest('REVIEW_TIME_OFF_REQUEST', handleReviewTimeOff);
    yield takeLatest('DELETE_TIME_OFF_REQUEST', handleDeleteTimeOff);

    // Holiday Mappings
    yield takeLatest('GET_HOLIDAYS_REQUEST', handleGetHolidays);
    yield takeLatest('ADD_HOLIDAY_REQUEST', handleAddHoliday);
    yield takeLatest('EDIT_HOLIDAY_REQUEST', handleEditHoliday);
    yield takeLatest('DELETE_HOLIDAY_REQUEST', handleDeleteHoliday);

    // Report Mappings
    yield takeLatest('GET_MASTER_REPORT_REQUEST', handleGetMasterReport);
    yield takeLatest('GET_MASTER_REPORT_TASK_REQUEST', handleGetMasterReportTask);
    yield takeLatest('GET_REPORT_CLIENTS_REQUEST', handleGetReportClients);
    yield takeLatest('GET_REPORT_ACCOUNTS_REQUEST', handleGetReportAccounts);
    yield takeLatest('GET_REPORT_USERS_REQUEST', handleGetReportUsers);
    yield takeLatest('GET_RETAINERS_REQUEST', handleGetRetainers);
    yield takeLatest('GET_PROJECT_PROFITABILITY_REQUEST', handleGetProjectProfitability);
    yield takeLatest('GET_RESOURCE_UTILIZATION_REQUEST', handleGetResourceUtilization);
    yield takeLatest('GET_DAILY_HOURS_REQUEST', handleGetDailyHours);
    yield takeEvery('GET_MY_DAILY_HOURS_REQUEST', handleGetMyDailyHours);
    yield takeLatest('GET_REPORT_DEPARTMENTS_REQUEST', handleGetReportDepartments);
    yield takeLatest('GET_AR_AGING_REQUEST', handleGetArAging);
    yield takeLatest('GET_PROFITABILITY_TREND_REQUEST', handleGetProfitabilityTrend);
    yield takeLatest('GET_HOURS_POSTED_REQUEST', handleGetHoursPosted);

    // Charges Mappings
    yield takeLatest('GET_CHARGES_REQUEST', handleGetCharges);
    yield takeLatest('GET_CHARGE_REQUEST', handleGetCharge);
    yield takeLatest('CREATE_CHARGE_REQUEST', handleCreateCharge);
    yield takeLatest('UPDATE_CHARGE_REQUEST', handleUpdateCharge);
    yield takeLatest('DELETE_CHARGE_REQUEST', handleDeleteCharge);
    yield takeLatest('ACTIVATE_CHARGE_REQUEST', handleActivateCharge);
    yield takeLatest('DEACTIVATE_CHARGE_REQUEST', handleDeactivateCharge);
    
    // Invoice Mappings
    yield takeEvery('GET_INVOICES_REQUEST', handleGetInvoices);
    yield takeLatest('GET_INVOICES_BY_BILLING_ENTITIES_REQUEST', handleGetInvoicesByBillingEntities);
    yield takeLatest('GET_INVOICE_REQUEST', handleGetInvoice);
    yield takeLatest('CREATE_INVOICE_REQUEST', handleCreateInvoice);
    yield takeLatest('UPDATE_INVOICE_REQUEST', handleUpdateInvoice);
    yield takeLatest('UPDATE_INVOICE_LINE_ITEMS_REQUEST', handleUpdateInvoiceLineItems);
    yield takeLatest('SEND_INVOICE_REQUEST', handleSendInvoice);
    yield takeLatest('CANCEL_INVOICE_REQUEST', handleCancelInvoice);
    yield takeLatest('UPDATE_INVOICE_STATUS_REQUEST', handleUpdateInvoiceStatus);
    yield takeLatest('CREATE_CREDIT_MEMO_REQUEST', handleCreateCreditMemo);
    yield takeLatest('EXPORT_INVOICES_IIF_REQUEST', handleExportInvoicesIif);
    yield takeLatest('GENERATE_LAST_MONTH_INVOICES_REQUEST', handleGenerateLastMonthInvoices);
    yield takeLatest('SEND_INVOICE_TO_GATEWAY_REQUEST', handleSendInvoiceToGateway);
    yield takeLatest('DELETE_INVOICE_REQUEST', handleDeleteInvoice);
    yield takeLatest('GET_CLIENTS_WITH_RECENT_INVOICES_REQUEST', handleGetClientsWithRecentInvoices);
    yield takeLatest('GET_INACTIVE_CLIENTS_REQUEST', handleGetInactiveClients);
    yield takeLatest('GET_INVOICE_SUMMARY_REQUEST', handleGetInvoiceSummary);
    yield takeLatest('GET_CLIENT_BILLING_OVERVIEW_REQUEST', handleGetClientBillingOverview);
    yield takeLatest('RECORD_CLIENT_PAYMENT_REQUEST', handleRecordClientPayment);
    yield takeLatest('SEND_ALL_DRAFT_INVOICES_REQUEST', handleSendAllDraftInvoices);
    yield takeEvery('GET_INVOICE_ORPHANS_REQUEST', handleGetInvoiceOrphans);
    yield takeEvery('ADD_INVOICE_ORPHANS_REQUEST', handleAddInvoiceOrphans);
    yield takeLatest('GET_PDF_TEMPLATE_CONFIG_REQUEST', handleGetPdfTemplateConfig);
    yield takeEvery('SAVE_PDF_TEMPLATE_CONFIG_REQUEST', handleSavePdfTemplateConfig);
    yield takeEvery('UPLOAD_PDF_TEMPLATE_REQUEST', handleUploadPdfTemplate);
    yield takeEvery('DOWNLOAD_INVOICE_PDF_REQUEST', handleDownloadInvoicePdf);
    yield takeEvery('GET_INTUIT_CONNECTION_STATUS_REQUEST', handleGetIntuitConnectionStatus);
    yield takeEvery('GET_INTUIT_CUSTOMERS_REQUEST', handleGetIntuitCustomers);
    yield takeEvery('GET_INVOICED_PERIOD_FOR_DATE_REQUEST', handleGetInvoicedPeriodForDate);
    yield takeLatest('GET_UNBILLED_REPORT_REQUEST', handleGetUnbilledReport);
    yield takeEvery('GET_BILLING_PREVIEW_REQUEST', handleGetBillingPreview);
    yield takeLatest('GET_INVOICE_NUMBER_PREVIEW_REQUEST', handleGetInvoiceNumberPreview);
    yield takeLatest('SEARCH_BILLING_ENTITIES_REQUEST', handleSearchBillingEntities);
    yield takeEvery('TOGGLE_BILLING_ENTITY_REQUEST', handleToggleBillingEntity);
    yield takeLatest('GET_BILLING_RETAINERS_REQUEST', handleGetBillingRetainers);
    yield takeEvery('SAVE_BILLING_RETAINER_TERMS_REQUEST', handleSaveBillingRetainerTerms);
    yield takeEvery('GET_RETAINER_STATUS_REQUEST', handleGetRetainerStatus);

    yield takeLatest('GET_BILLING_EMPLOYEES_REQUEST', handleGetBillingEmployees);
    yield takeEvery('UPDATE_EMPLOYEE_SALARY_DATA_REQUEST', handleUpdateEmployeeSalaryData);
    yield takeEvery('SET_EMPLOYEE_TERMINATED_REQUEST', handleSetEmployeeTerminated);
    yield takeLatest('GET_EMPLOYEE_PAYMENTS_REQUEST', handleGetEmployeePayments);
    yield takeEvery('CREATE_EMPLOYEE_PAYMENT_REQUEST', handleCreateEmployeePayment);
    yield takeEvery('UPDATE_EMPLOYEE_PAYMENT_REQUEST', handleUpdateEmployeePayment);
    yield takeEvery('DELETE_EMPLOYEE_PAYMENT_REQUEST', handleDeleteEmployeePayment);

    // Fixed Price Payments
    yield takeEvery('GET_FIXED_PRICE_PAYMENTS_REQUEST', handleGetFixedPricePayments);
    yield takeEvery('UPDATE_FIXED_PRICE_PAYMENT_REQUEST', handleUpdateFixedPricePayment);
    yield takeEvery('DELETE_FIXED_PRICE_PAYMENT_REQUEST', handleDeleteFixedPricePayment);
    yield takeEvery('CLEAR_FIXED_PRICE_PAYMENTS_REQUEST', handleClearFixedPricePayments);
    yield takeEvery('GET_UNCONFIGURED_FIXED_PRICE_ENTITIES_REQUEST', handleGetUnconfiguredFixedPriceEntities);

    // Role Mappings
    yield takeLatest('GET_ROLES_REQUEST', handleGetRoles);
    yield takeLatest('GET_ROLE_REQUEST', handleGetRole);
    yield takeLatest('GET_ROLE_EXPANDED_PERMISSIONS_REQUEST', handleGetRoleExpandedPermissions);
    yield takeLatest('GET_ROLE_USERS_REQUEST', handleGetRoleUsers);
    yield takeLatest('CREATE_ROLE_REQUEST', handleCreateRole);
    yield takeLatest('UPDATE_ROLE_REQUEST', handleUpdateRole);
    yield takeLatest('DELETE_ROLE_REQUEST', handleDeleteRole);

    // Permission Mappings
    yield takeLatest('GET_PERMISSIONS_REQUEST', handleGetPermissions);
    yield takeEvery('GET_VISIBILITY_CHECK_REQUEST', handleGetVisibilityCheck);

    // Register Mappings
    yield takeLatest(checkEmailAvailability.type, handleCheckEmailAvailability);

    // Notification Mappings
    yield takeEvery('GET_NOTIFICATIONS_REQUEST', handleGetNotifications);
    yield takeEvery('GET_UNREAD_COUNT_REQUEST', handleGetUnreadCount);
    yield takeLatest('MARK_NOTIFICATION_READ_REQUEST', handleMarkNotificationRead);
    yield takeLatest('MARK_ALL_NOTIFICATIONS_READ_REQUEST', handleMarkAllNotificationsRead);
    yield takeLatest('DELETE_NOTIFICATION_REQUEST', handleDeleteNotification);
    yield takeLatest('SEND_CUSTOM_NOTIFICATION_REQUEST', handleSendCustomNotification);

    // Template Mappings
    // NOTE: must allow parallel fetches (group+project lists)
    yield takeEvery('GET_TEMPLATES_REQUEST', handleGetTemplates);
    yield takeLatest('GET_GROUP_TEMPLATE_REQUEST', handleGetGroupTemplate);
    yield takeLatest('GET_PROJECT_TEMPLATE_REQUEST', handleGetProjectTemplate);
    yield takeLatest('GET_TASK_TEMPLATE_REQUEST', handleGetTaskTemplate);
    yield takeEvery('GET_CHECKLIST_TEMPLATE_REQUEST', handleGetChecklistTemplate);
    yield takeLatest('CREATE_CHECKLIST_TEMPLATE_REQUEST', handleCreateChecklistTemplate);
    yield takeLatest('UPDATE_CHECKLIST_TEMPLATE_REQUEST', handleUpdateChecklistTemplate);
    yield takeLatest('DELETE_CHECKLIST_TEMPLATE_REQUEST', handleDeleteChecklistTemplate);
    yield takeEvery('GET_QUOTE_TEMPLATE_REQUEST', handleGetQuoteTemplate);
    yield takeLatest('CREATE_QUOTE_TEMPLATE_REQUEST', handleCreateQuoteTemplate);
    yield takeLatest('UPDATE_QUOTE_TEMPLATE_REQUEST', handleUpdateQuoteTemplate);
    yield takeLatest('DELETE_QUOTE_TEMPLATE_REQUEST', handleDeleteQuoteTemplate);
    yield takeLatest('CREATE_QUOTE_TEMPLATE_FROM_QUOTE_REQUEST', handleCreateQuoteTemplateFromQuote);
    yield takeLatest('CREATE_GROUP_TEMPLATE_REQUEST', handleCreateGroupTemplate);
    yield takeLatest('CREATE_PROJECT_TEMPLATE_REQUEST', handleCreateProjectTemplate);
    yield takeLatest('CREATE_TASK_TEMPLATE_REQUEST', handleCreateTaskTemplate);
    yield takeLatest('UPDATE_GROUP_TEMPLATE_REQUEST', handleUpdateGroupTemplate);
    yield takeLatest('UPDATE_PROJECT_TEMPLATE_REQUEST', handleUpdateProjectTemplate);
    yield takeLatest('UPDATE_TASK_TEMPLATE_REQUEST', handleUpdateTaskTemplate);
    yield takeLatest('DELETE_GROUP_TEMPLATE_REQUEST', handleDeleteGroupTemplate);
    yield takeLatest('DELETE_PROJECT_TEMPLATE_REQUEST', handleDeleteProjectTemplate);
    yield takeLatest('DELETE_TASK_TEMPLATE_REQUEST', handleDeleteTaskTemplate);
    yield takeLatest('EXECUTE_GROUP_TEMPLATE_REQUEST', handleExecuteGroupTemplate);
    yield takeLatest('EXECUTE_PROJECT_TEMPLATE_REQUEST', handleExecuteProjectTemplate);
    yield takeLatest('GET_TEMPLATE_IMPORT_SOURCE_REQUEST', handleGetTemplateImportSource);
    yield takeLatest('IMPORT_TEMPLATE_REQUEST', handleImportTemplate);

    // Search
    yield takeLatest('SEARCH_REQUEST', handleSearch);

    // Quote Mappings
    yield takeLatest(getQuotes.type, handleGetQuotes);
    yield takeLatest(getQuote.type, handleGetQuote);
    yield takeLatest(createQuote.type, handleCreateQuote);
    yield takeLatest(updateQuote.type, handleUpdateQuote);
    yield takeLatest(approveQuote.type, handleApproveQuote);
    yield takeLatest(rejectQuote.type, handleRejectQuote);
    yield takeLatest(acceptQuote.type, handleAcceptQuote);
    yield takeLatest(declineQuote.type, handleDeclineQuote);
    yield takeLatest(previewQuotePdf.type, handlePreviewQuotePdf);
    yield takeLatest(reopenQuote.type, handleReopenQuote);
    yield takeLatest(cloneQuote.type, handleCloneQuote);

    // Service Account Mappings
    yield takeLatest('GET_SERVICE_ACCOUNTS_REQUEST', handleGetServiceAccounts);
    yield takeLatest('CREATE_SERVICE_ACCOUNT_REQUEST', handleCreateServiceAccount);
    yield takeLatest('ROTATE_SERVICE_ACCOUNT_REQUEST', handleRotateServiceAccount);
    yield takeLatest('ACTIVATE_SERVICE_ACCOUNT_REQUEST', handleActivateServiceAccount);
    yield takeLatest('DEACTIVATE_SERVICE_ACCOUNT_REQUEST', handleDeactivateServiceAccount);
    yield takeLatest('DELETE_SERVICE_ACCOUNT_REQUEST', handleDeleteServiceAccount);

    // API Key Mappings
    yield takeEvery('GET_API_KEY_SCOPES_REQUEST', handleGetApiKeyScopes);
    yield takeEvery('GET_MY_API_KEYS_REQUEST', handleGetMyApiKeys);
    yield takeEvery('CREATE_API_KEY_REQUEST', handleCreateApiKey);
    yield takeEvery('REVOKE_MY_API_KEY_REQUEST', handleRevokeMyApiKey);
    yield takeEvery('GET_ALL_API_KEYS_REQUEST', handleGetAllApiKeys);
    yield takeEvery('GET_USER_API_KEYS_REQUEST', handleGetUserApiKeys);
    yield takeEvery('REVOKE_USER_API_KEY_REQUEST', handleRevokeUserApiKey);
    yield takeEvery('DELETE_USER_API_KEY_REQUEST', handleDeleteUserApiKey);

    // Inbound Mail Mappings
    yield takeLatest('GET_INBOUND_MAIL_REQUEST', handleGetInboundMail);
    yield takeLatest('RESOLVE_INBOUND_MAIL_REQUEST', handleResolveInboundMail);
    yield takeLatest('GET_INBOUND_MAIL_OAUTH_STATUS_REQUEST', handleGetInboundMailOauthStatus);

    // Tag Mappings
    yield takeEvery('GET_TAGS_REQUEST', handleGetTags);
    yield takeEvery('GET_ASSIGNABLE_TAGS_REQUEST', handleGetAssignableTags);
    yield takeEvery('CREATE_TAG_REQUEST', handleCreateTag);
    yield takeEvery('UPDATE_TAG_REQUEST', handleUpdateTag);
    yield takeEvery('DELETE_TAG_REQUEST', handleDeleteTag);
    yield takeEvery('RESTORE_TAG_REQUEST', handleRestoreTag);
    yield takeLatest('GET_TAG_USAGE_REQUEST', handleGetTagUsage);
    yield takeEvery('SET_TAG_ASSIGNMENTS_REQUEST', handleSetTagAssignments);
    yield takeEvery('GET_INHERITED_TAGS_REQUEST', handleGetInheritedTags);
    yield takeEvery('GET_TAG_DETAIL_REQUEST', handleGetTagDetail);
    yield takeEvery('UPDATE_TAG_CONTEXT_REQUEST', handleUpdateTagContext);

    // Push Subscriptions
    yield takeEvery('GET_VAPID_PUBLIC_KEY_REQUEST', handleGetVapidPublicKey);
    yield takeEvery('SUBSCRIBE_PUSH_REQUEST', handleSubscribePush);
    yield takeEvery('UNSUBSCRIBE_PUSH_REQUEST', handleUnsubscribePush);
    yield takeLatest('GET_PUSH_STATUS_REQUEST', handleGetPushStatus);

    // Calendar Mappings
    yield takeLatest(getCalendarStatus.type, handleGetCalendarStatus);
    yield takeLatest('GET_MEETINGS_REQUEST', handleGetMeetings);
    yield takeLatest('GET_MANAGER_REPORT_REQUEST', handleGetManagerReport);
    yield takeEvery('SCHEDULE_MEETING_REQUEST', handleScheduleMeeting);
    yield takeLatest('GET_MEETING_SLOTS_REQUEST', handleGetMeetingSlots);
    yield takeEvery('RECORD_MEETING_TIME_REQUEST', handleRecordMeetingTime);
    yield takeEvery('MANUAL_TRACK_MEETING_REQUEST', handleSetMeetingManualTrack);
    yield takeLatest('MANAGER_REPORT_DRILLDOWN_REQUEST', handleGetManagerReportDrilldown);
    yield takeEvery('SET_USER_CALENDAR_OPT_OUT_REQUEST', handleSetUserCalendarOptOut);

    // BugHerd Mappings
    yield takeEvery('GET_BUGHERD_CONTEXT_REQUEST', handleGetBugherdContext);
    yield takeEvery('GET_BUGHERD_BOARDS_REQUEST', handleGetBugherdBoards);
    yield takeEvery('SYNC_BUGHERD_BOARD_REQUEST', handleSyncBugherdBoard);
    yield takeEvery('DISCONNECT_BUGHERD_REQUEST', handleDisconnectBugherd);

    // KB Article Mappings
    yield takeLatest('GET_KB_TAGS_REQUEST', handleGetKBTags);
    yield takeLatest('GET_KB_ARTICLES_REQUEST', handleGetKBArticles);
    yield takeLatest('GET_KB_ARTICLE_REQUEST', handleGetKBArticle);
    yield takeLatest('CREATE_KB_ARTICLE_REQUEST', handleCreateKBArticle);
    yield takeLatest('CREATE_KB_ARTICLE_FROM_PROJECT_REQUEST', handleCreateKBArticleFromProject);
    yield takeLatest('REQUEST_KB_AI_REWRITE_REQUEST', handleRequestKBAIRewrite);
    yield takeLatest('UPDATE_KB_ARTICLE_REQUEST', handleUpdateKBArticle);
    yield takeLatest('DELETE_KB_ARTICLE_REQUEST', handleDeleteKBArticle);
    yield takeLatest('GET_KB_ARTICLE_VERSIONS_REQUEST', handleGetKBArticleVersions);
}
